import React from 'react'
import isEmpty from 'lodash-es/isEmpty'

import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles'

import Grid from '@material-ui/core/Grid'
import ButtonBase from '@material-ui/core/ButtonBase'
import Typography from '@material-ui/core/Typography'

import { Action } from 'types'
import { GameHistoryResponse, GeneralResponse, IArchiveGame } from 'protos'

import T from 'components/FormattedMessage'
import QuizCard from 'components/GameCard/components/QuizCard'
import FancyDivider from 'components/FancyDivider'
import ActionContext from 'components/ActionContext'
import { QuizToggleContext } from 'contexts/QuizToggleContext'
import CompletedGamesEmptyState from '../../components/CompletedGamesEmptyState'

import tickIconUrl from './assets/tick.svg'
import GameCardButton from './GameCardButton'

interface Props {
  getGameHistory: () => Promise<GameHistoryResponse>
  acknowlegdeAllGameResult: () => Promise<GeneralResponse>
  getUrlForGameDetails: (gameId: string) => string
}

type State = {
  gameHistory: Action<GameHistoryResponse | null>
}

const styles = createStyles({
  divider: {
    paddingLeft: 20,
    paddingRight: 20,
  },
  card: {
    margin: 20,
  },
})

// NOTE: this is an experimental generalization of the
// state handling of the actions, should be moved into
// its own domain if it serves us well
const executeAction = <U extends object>(
  promise: Promise<U>,
  setState: (state: Action<U>) => void,
) => {
  promise
    .then((result: U) => {
      setState({ isLoading: false, result })
    })
    .catch((err: Error) => {
      setState({ isLoading: false, errorCode: err.message })
    })
}

type InjectedProps = Props & WithStyles<typeof styles>

class GameHistoryTab extends React.Component<InjectedProps, State> {
  constructor(props: InjectedProps) {
    super(props)
    this.state = {
      gameHistory: { isLoading: true },
    }
  }

  componentDidMount() {
    this.requestGameHistory()
  }

  private requestGameHistory = () => {
    executeAction(
      this.props.getGameHistory(),
      (gameHistory) => this.setState({ gameHistory }),
    )
  }

  private ackAllResults = () => {
    this.props.acknowlegdeAllGameResult()
      .then(() => { this.requestGameHistory() })
  }

  private renderEmptyState = () => <CompletedGamesEmptyState />

  private renderGames = (archiveGames: IArchiveGame[], adCounter: number) => {
    const { isQuizOpenedByUser, closeQuiz, openQuiz } = React.useContext(QuizToggleContext)

    const { classes } = this.props

    return archiveGames.map((archiveGame, i: number) => {
      const { game, quiz } = archiveGame

      const adIndex = i + adCounter
      const hasAdSlot = (adIndex === 2 || adIndex === 9)
      const hasActiveQuiz = !isEmpty(quiz)

      if (hasActiveQuiz && isQuizOpenedByUser(game!.gameId!)) {
        return (
          <QuizCard
            key={game!.gameId!}
            gameId={game!.gameId!}
            onClose={() => { closeQuiz(game!.gameId!) }}
            homeTeam={game!.match!.homeTeam!.name!}
            awayTeam={game!.match!.awayTeam!.name!}
            className={classes.card}
            questions={quiz!}
            readonly
          />
        )
      }

      return (
        <GameCardButton
          key={game!.gameId!}
          adIndex={adIndex}
          openQuiz={openQuiz}
          hasAdSlot={hasAdSlot}
          className={classes.card}
          archiveGame={archiveGame}
          gameDetailsUrl={this.props.getUrlForGameDetails(game!.gameId!)}
        />
      )
    })
  }

  private renderHistory = () => {
    const { classes } = this.props
    const { gameHistory } = this.state

    const ackedGames = gameHistory.result!.game
    const unackedGames = gameHistory.result!.unackedGame

    return (
      <>
        {this.renderGames(unackedGames, 0)}
        {ackedGames.length > 0 && unackedGames.length > 0 && (
          <FancyDivider className={classes.divider}>
            <T id="gamehistory_section_acknowledged" />
          </FancyDivider>
        )}
        {this.renderGames(ackedGames, unackedGames.length)}
      </>
    )
  }

  private renderContent = () => {
    const { gameHistory } = this.state

    if (!gameHistory.result) {
      return null
    }

    const games = gameHistory.result.game
    const unackedGames = gameHistory.result.unackedGame
    const isEmptyGameList = games.length === 0 && unackedGames.length === 0

    return (
      <>
        {unackedGames.length > 0 && (
          <>
            <ButtonBase
              component="div"
              style={{ margin: '16px 0', paddingTop: 16, display: 'block' }}
              onClick={this.ackAllResults}
            >
              <Grid container justify="center">
                <Grid item>
                  <img alt="" src={tickIconUrl} style={{ display: 'block', marginRight: 8 }} />
                </Grid>
                <Grid item>
                  <Typography variant="body1" align="center">
                    <T id="gamehistory_action_acknowledge_all" />
                  </Typography>
                </Grid>
              </Grid>
            </ButtonBase>
          </>
        )}
        {isEmptyGameList
          ? this.renderEmptyState()
          : this.renderHistory()}
      </>
    )
  }

  render() {
    const { gameHistory } = this.state

    return (
      <ActionContext
        isLoading={gameHistory.isLoading}
        errorCode={gameHistory.errorCode}
        component={this.renderContent}
        componentProps={{}}
      />
    )
  }
}

export default withStyles(styles)(GameHistoryTab)
